import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Toaster as SonnerToaster } from 'sonner';

/**
 * Область уведомлений тянется от боковой панели до правого края окна, а уведомление
 * встаёт по её центру: так оно оказывается внизу по центру рабочей области при любой
 * ширине боковой панели, как .toast макета. Отступ --ws-toast-inset равен ширине панели
 * там, где она есть (index.css). Правила sonner для узких экранов (во всю ширину окна,
 * без сдвига, свой нижний отступ) этим же перекрываются: у макета отступ снизу один на
 * всех ширинах.
 */
const REGION_STYLE: CSSProperties = {
	left: 'var(--ws-toast-inset)',
	right: 0,
	bottom: 'var(--offset-bottom)',
	width: 'auto',
	transform: 'none',
	fontFamily: 'inherit',
};

/** Уведомление по ширине содержимого, по центру области; границы ширины задаёт .ws-toast */
const TOAST_STYLE: CSSProperties = {
	left: 0,
	right: 0,
	marginInline: 'auto',
	width: 'fit-content',
};

/**
 * Область уведомлений приложения.
 *
 * Вид и размеры уведомления задаёт класс .ws-toast, перенесённый из макета
 * (docs/design/frontend-v2/prototype.css:1364-1379): тёмная плашка без значка, без
 * кнопки закрытия и без заливки по виду -- отказ и успех различает текст. Sonner отдаёт
 * ей разметку без собственных стилей (unstyled), а положение берёт на себя. Как и в
 * макете, видно одно уведомление: новое заменяет прежнее.
 *
 * Отдельный компонент ради useTranslation: подписи для программ экранного
 * доступа, вычисленные в App, при смене языка не обновились бы -- App на
 * неё не подписан. Без подписей sonner ставит свои английские.
 */
export function Toaster() {
	const { t } = useTranslation();

	return (
		<SonnerToaster
			position="bottom-center"
			visibleToasts={1}
			icons={{ success: null, error: null, info: null, warning: null, loading: null }}
			style={REGION_STYLE}
			containerAriaLabel={t('notifications.label')}
			toastOptions={{
				unstyled: true,
				style: TOAST_STYLE,
				classNames: { toast: 'ws-toast', actionButton: 'ws-btn mt-2' },
			}}
		/>
	);
}
